import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Icon } from '../lib/icons';
import { PreferenceToggles } from '../lib/PreferencesProvider';
import { PasswordField, usePasswordReveal } from '../components/PasswordField';

/**
 * Sign-in.
 *
 * The prototype has no login screen at all — identity came from an in-app role
 * switcher, which FEAT-001 replaces with a real session. So this is one of the
 * few genuinely new screens, and it is built entirely from the prototype's own
 * primitives (`card`, `field`, `btn`, `empty`) so it belongs to the same
 * interface rather than looking bolted on.
 */
export function LoginPage(): ReactElement {
  const { t } = useTranslation();
  const { user, signIn } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  /*
   * Newly worth having: a temporary password from an invitation is an
   * unfamiliar string somebody is transcribing from an email, and a mistyped
   * character is indistinguishable from a wrong password — five of which lock
   * the account (REQ-006).
   */
  const reveal = usePasswordReveal();
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  // Set by `SetPasswordPage` after a successful reset. Announced rather than
  // merely shown: the user has just been signed out of every session, and
  // arriving at a login screen with no explanation reads as a failure.
  const passwordSet = (location.state as { passwordSet?: boolean } | null)?.passwordSet === true;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setRetryAfter(null);
    setIsSubmitting(true);

    try {
      /*
       * Hidden again before the navigation. Leaving a revealed password on
       * screen through the page transition is exactly when somebody else is
       * looking at a shared or projected display.
       */
      reveal.hide();
      await signIn(email, password, rememberMe);
    } catch (caught) {
      if (caught instanceof ApiClientError) {
        /*
         * The server has already localised the message, so it is rendered as
         * given. Behaviour keys off the stable `code` instead — branching on
         * message text would break the moment the user switches language.
         */
        setError(caught.message);

        if (caught.code === 'account_locked') {
          const seconds = Number(caught.meta.retry_after ?? 0);
          if (seconds > 0) setRetryAfter(Math.ceil(seconds / 60));
        }
      } else {
        setError(t('login_network_error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="card login-card">
        <div className="login-brand">
          <img src="/logo.png" alt="Boud" />
          <div className="sub">{t('boud_sub')}</div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <h1 className="login-title">{t('sign_in')}</h1>

          {/*
            `role="alert"` announces the failure to a screen reader the moment it
            appears. Without it the form simply looks unchanged to anyone not
            watching the pixels.
          */}
          {passwordSet ? (
            <div className="login-ok" role="status">
              <Icon name="check" />
              <span>{t('password_set_now_sign_in')}</span>
            </div>
          ) : null}

          {error ? (
            <div className="login-error" role="alert">
              <Icon name="info" />
              <span>
                {error}
                {retryAfter ? ` ${t('try_again_in', { minutes: retryAfter })}` : ''}
              </span>
            </div>
          ) : null}

          <div className="field">
            <label htmlFor="email">{t('email')}</label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <PasswordField
            id="password"
            label={t('password')}
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            disabled={isSubmitting}
            revealed={reveal.revealed}
            onRevealedChange={reveal.setRevealed}
          />

          <label className="login-remember">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isSubmitting}
            />
            <span>{t('remember_me')}</span>
          </label>

          <button type="submit" className="btn primary login-submit" disabled={isSubmitting}>
            {isSubmitting ? t('signing_in') : t('sign_in')}
          </button>

          <Link className="login-alt" to="/forgot-password">
            {t('forgot_password')}
          </Link>
        </form>

        {/*
          Theme and language are reachable before signing in. A user whose
          interface should be in Arabic, or who cannot read the dark theme
          comfortably, should not have to authenticate first to fix that.
        */}
        <div className="login-toggles">
          <PreferenceToggles />
        </div>
      </div>
    </div>
  );
}
